import type { PublicMailTemplate } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { RichTextEditor } from '../components/mail/identity-signature-sheet'
import { Button } from '../components/ui/button'
import { SidePanel } from '../components/ui/side-panel'
import { api } from '../lib/api'
import { notify } from '../lib/notify'

export const canSaveMailTemplate = (title: string, companyIds: string[]) =>
  !!title.trim() && companyIds.length > 0

export function MailTemplatesSettings() {
  const qc = useQueryClient()
  const { data: firms } = useQuery({ queryKey: ['mail-companies'], queryFn: api.listMailCompanies })
  const { data } = useQuery({
    queryKey: ['mail-templates'],
    queryFn: () => api.listMailTemplates(),
  })
  const [editing, setEditing] = useState<PublicMailTemplate | 'new' | null>(null)
  const [title, setTitle] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [companyIds, setCompanyIds] = useState<string[]>([])
  const [autoAttachSignature, setAutoAttachSignature] = useState(true)
  const open = (template: PublicMailTemplate | 'new') => {
    setEditing(template)
    setTitle(template === 'new' ? '' : template.title)
    setBodyHtml(template === 'new' ? '' : template.bodyHtml)
    setCompanyIds(template === 'new' ? [] : template.companyIds)
    setAutoAttachSignature(template === 'new' ? true : template.autoAttachSignature)
  }
  const save = useMutation({
    mutationFn: () => {
      const input = { title, bodyHtml, companyIds, autoAttachSignature }
      return editing && editing !== 'new'
        ? api.updateMailTemplate(editing.id, input)
        : api.createMailTemplate(input)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail-templates'] })
      setEditing(null)
      notify.success('Vorlage gespeichert')
    },
    onError: (error: Error) => notify.error(error.message),
  })
  const remove = useMutation({
    mutationFn: api.deleteMailTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mail-templates'] })
      setEditing(null)
    },
    onError: (error: Error) => notify.error(error.message),
  })
  return (
    <section
      id="mail-templates"
      className="space-y-3 rounded-2xl border border-border-default bg-bg-elevated p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold">Textvorlagen</h2>
          <p className="text-xs text-text-secondary">
            Eine Vorlage darf mehreren Firmen zugeordnet sein.
          </p>
        </div>
        <Button size="sm" onClick={() => open('new')}>
          Hinzufügen
        </Button>
      </div>
      {firms?.companies.map((company) => {
        const templates =
          data?.templates.filter((template) => template.companyIds.includes(company.id)) ?? []
        return (
          <div key={company.id} className="overflow-hidden rounded-xl border border-border-subtle">
            <h3 className="border-b border-border-subtle px-3 py-2 text-sm font-medium">
              {company.name}{' '}
              <span className="float-right text-xs text-text-tertiary">
                {templates.length} Vorlagen
              </span>
            </h3>
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => open(template)}
                className="block w-full px-3 py-2 text-left hover:bg-bg-overlay"
              >
                <span className="block text-sm font-medium">{template.title}</span>
                <span className="block truncate text-xs text-text-tertiary">
                  {template.bodyText}
                </span>
              </button>
            ))}
          </div>
        )
      })}
      <SidePanel
        open={!!editing}
        className="h-[605px] sm:h-auto"
        description="Textvorlage bearbeiten"
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Textvorlage anlegen' : title}
        footer={
          <div className="flex w-full justify-end gap-6">
            {editing && editing !== 'new' && (
              <Button variant="ghost" onClick={() => remove.mutate(editing.id)}>
                Löschen
              </Button>
            )}
            <Button
              disabled={save.isPending || !canSaveMailTemplate(title, companyIds)}
              onClick={() => save.mutate()}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <label className="block text-sm font-medium">
            Titel
            <input
              className="mt-1 w-full rounded-lg border border-border-default bg-bg-overlay p-2"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <div>
            <p className="mb-2 text-sm font-medium">Firmenzuordnung</p>
            <div className="flex flex-wrap gap-1.5">
              {firms?.companies.map((company) => (
                <button
                  key={company.id}
                  type="button"
                  aria-pressed={companyIds.includes(company.id)}
                  onClick={() =>
                    setCompanyIds((ids) =>
                      ids.includes(company.id)
                        ? ids.filter((id) => id !== company.id)
                        : [...ids, company.id],
                    )
                  }
                  className={`no-min-tap rounded-full border px-2.5 py-1.5 text-xs ${companyIds.includes(company.id) ? 'border-transparent bg-accent/15 text-accent' : 'border-border-default text-text-secondary'}`}
                >
                  {company.name}
                </button>
              ))}
            </div>
            {companyIds.length === 0 && (
              <p role="alert" className="mt-1 text-xs text-warning">
                Mindestens eine Firma wählen.
              </p>
            )}
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Text</p>
            <RichTextEditor
              key={editing === 'new' ? 'new' : editing?.id}
              value={bodyHtml}
              onChange={setBodyHtml}
              label="Vorlagentext"
              minHeight="min-h-[136px]"
            />
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-3 text-sm">
            <span>
              <span>Signatur automatisch anhängen</span>
              <span className="block text-xs text-text-tertiary">
                Fügt die Signatur der gewählten Firma unter den Text ein.
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-label="Signatur automatisch anhängen"
              aria-checked={autoAttachSignature}
              onClick={() => setAutoAttachSignature((value) => !value)}
              className="relative flex h-11 w-11 shrink-0 items-center justify-center"
            >
              <span
                className={`relative h-6 w-10 rounded-full transition-colors ${autoAttachSignature ? 'bg-accent' : 'bg-text-tertiary/40'}`}
              >
                <span
                  className={`absolute left-[3px] top-[3px] h-[18px] w-[18px] rounded-full bg-white transition-transform ${autoAttachSignature ? 'translate-x-4' : ''}`}
                />
              </span>
            </button>
          </div>
        </div>
      </SidePanel>
    </section>
  )
}
